import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth, isLocationUser, homeLocationOf } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError, fmtNum, LOCATIONS } from "@/lib/utils";
import { Truck, CheckCircle2, Pencil, Search, Save, X, Ship, Info, FlaskConical, ArrowRightLeft, Blend } from "lucide-react";

const KINDS = [
  { value: "domestic", label: "DOMESTIC", icon: Truck },
  { value: "export", label: "EXPORT", icon: Ship },
];

export default function Dispatch() {
  const { user } = useAuth();
  const home = homeLocationOf(user);
  const locked = isLocationUser(user);
  const [fromLocation, setFromLocation] = useState(home || "FACTORY");
  const [batches, setBatches] = useState([]);
  const [picks, setPicks] = useState({});

  // Form fields
  const [kind, setKind] = useState("domestic");
  const [customer, setCustomer] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [dispatchBatchNo, setDispatchBatchNo] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [notes, setNotes] = useState("");

  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [detail, setDetail] = useState(null);       // full-traceability detail being viewed
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    const [b, d] = await Promise.all([
      api.get("/batches", { params: { status: "approved" } }),
      api.get("/dispatches"),
    ]);
    setBatches(b.data.filter((x) => x.bags_remaining > 0));
    setHistory(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const locBatches = useMemo(
    () => batches
      .filter((b) => (b.location || "FACTORY") === fromLocation)
      .sort((a, b) => a.production_date.localeCompare(b.production_date)),
    [batches, fromLocation]
  );

  const updatePick = (b, val) => {
    const num = parseInt(val, 10);
    const next = { ...picks };
    if (!val || isNaN(num) || num <= 0) delete next[b.id];
    else next[b.id] = Math.min(num, b.bags_remaining);
    setPicks(next);
  };

  const selected = Object.entries(picks)
    .map(([bid, bags]) => {
      const b = batches.find((x) => x.id === bid);
      return b ? { b, bags } : null;
    })
    .filter(Boolean);
  const totalBags = selected.reduce((s, x) => s + x.bags, 0);

  const filteredHistory = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return history;
    return history.filter((d) => {
      const hay = [
        d.customer, d.vehicle, d.dispatch_batch_number,
        d.invoice_number, d.container_number, d.seal_number,
        d.kind, d.notes,
        ...(d.items || []).map((i) => i.batch_number),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [history, search]);

  const submit = async () => {
    setErr(""); setSuccess(null); setLoading(true);
    try {
      if (!customer.trim()) throw new Error("Customer is required");
      if (selected.length === 0) throw new Error("Pick at least one batch");
      if (kind === "export" && !containerNumber.trim()) {
        throw new Error("Container number is required for EXPORT dispatch");
      }
      const items = selected.map((x) => ({ batch_id: x.b.id, bags: x.bags }));
      const res = await api.post("/dispatches", {
        kind, customer, vehicle, dispatch_batch_number: dispatchBatchNo,
        invoice_number: invoiceNumber, container_number: containerNumber, seal_number: sealNumber,
        items, notes,
      });
      setSuccess(res.data);
      setPicks({});
      setCustomer(""); setVehicle(""); setDispatchBatchNo("");
      setInvoiceNumber(""); setContainerNumber(""); setSealNumber(""); setNotes("");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setEditErr("");
    setEditForm({
      kind: d.kind || "domestic",
      customer: d.customer || "",
      vehicle: d.vehicle || "",
      dispatch_batch_number: d.dispatch_batch_number || "",
      invoice_number: d.invoice_number || "",
      container_number: d.container_number || "",
      seal_number: d.seal_number || "",
      notes: d.notes || "",
    });
  };

  const openDetails = async (d) => {
    setDetailLoading(true);
    setDetail({ ...d, _loading: true });
    try {
      const r = await api.get(`/dispatches/${d.id}/details`);
      setDetail(r.data);
    } catch {
      setDetail(d); // fallback to the row data
    } finally {
      setDetailLoading(false);
    }
  };
  const closeEdit = () => { setEditingId(null); setEditForm({}); setEditErr(""); };
  const saveEdit = async () => {
    setEditErr(""); setEditLoading(true);
    try {
      if (!editForm.customer.trim()) throw new Error("Customer is required");
      if (editForm.kind === "export" && !editForm.container_number.trim()) {
        throw new Error("Container number is required for EXPORT");
      }
      await api.put(`/dispatches/${editingId}`, editForm);
      closeEdit();
      await load();
    } catch (e) {
      setEditErr(formatApiError(e));
    } finally {
      setEditLoading(false);
    }
  };

  const updateEdit = (field, value) => setEditForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="STORE MANAGER"
        title="Dispatch to Customer"
        description="Pick the source warehouse, choose DOMESTIC or EXPORT, then select batches and bags."
      />

      {success && (
        <div className="card p-4 mb-6 flex items-center gap-3" style={{ borderColor: "var(--green)" }} data-testid="dispatch-success">
          <CheckCircle2 size={20} color="var(--green)" />
          <div>
            <div className="font-medium">
              {success.kind === "export" ? "EXPORT" : "DOMESTIC"} dispatched to{" "}
              <span className="font-mono">{success.customer}</span>
            </div>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
              {success.total_bags} bags · {success.total_kg} kg
              {success.dispatch_batch_number ? ` · Dispatch Batch ${success.dispatch_batch_number}` : ""}
              {success.invoice_number ? ` · Invoice ${success.invoice_number}` : ""}
              {success.container_number ? ` · Container ${success.container_number}` : ""}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Source warehouse picker */}
        <div className="card p-5">
          <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>// SOURCE WAREHOUSE</div>
          <select
            value={fromLocation}
            onChange={(e) => setFromLocation(e.target.value)}
            disabled={locked}
            data-testid="dispatch-from-location"
          >
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          {locked && (
            <div className="text-xs font-mono mt-2" style={{ color: "var(--text-muted)" }}>
              Locked to your home warehouse.
            </div>
          )}
        </div>

        {/* Kind toggle */}
        <div className="card p-5 lg:col-span-2">
          <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>// DISPATCH TYPE</div>
          <div className="flex gap-2">
            {KINDS.map((k) => {
              const active = kind === k.value;
              const Icon = k.icon;
              return (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  className="btn flex-1"
                  style={{
                    background: active ? "var(--brand-blue)" : "#fff",
                    color: active ? "#fff" : "var(--text)",
                    border: `1px solid ${active ? "var(--brand-blue)" : "var(--border)"}`,
                  }}
                  data-testid={`kind-${k.value}-btn`}
                >
                  <Icon size={16} /> {k.label}
                </button>
              );
            })}
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
            {kind === "export"
              ? "EXPORT: invoice, container & seal numbers will be captured below."
              : "DOMESTIC: customer, vehicle and optional invoice/dispatch batch."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="card lg:col-span-2 overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="font-heading text-sm font-semibold">APPROVED BATCHES AT {fromLocation}</div>
          </div>
          <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
            <table className="table">
              <thead>
                <tr><th>Batch</th><th>Prod. Date</th><th className="text-right">Avail.</th><th>Bags to Dispatch</th></tr>
              </thead>
              <tbody>
                {locBatches.length === 0 && (
                  <tr><td colSpan="4" className="text-center py-8" style={{ color: "var(--text-muted)" }}>No available batches at {fromLocation}.</td></tr>
                )}
                {locBatches.map((b) => (
                  <tr key={b.id} data-testid={`dispatch-batch-${b.batch_number}`}>
                    <td className="font-mono">{b.batch_number}</td>
                    <td className="font-mono text-xs">{b.production_date}</td>
                    <td className="font-mono text-right">{b.bags_remaining}</td>
                    <td style={{ width: 140 }}>
                      <input
                        type="number" min="0" max={b.bags_remaining}
                        value={picks[b.id] || ""}
                        onChange={(e) => updatePick(b, e.target.value)}
                        placeholder="0"
                        style={{ padding: "4px 8px", fontFamily: "JetBrains Mono, monospace" }}
                        data-testid={`pick-bags-${b.batch_number}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalBags > 0 && (
            <div className="px-4 py-2 border-t font-mono text-xs" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              → PICKING <span style={{ color: "var(--brand-blue)" }}>{totalBags}</span> BAGS · {totalBags * 50} KG · FROM {fromLocation}
            </div>
          )}
          {err && (
            <div className="m-4 text-xs p-2 border" style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.08)" }} data-testid="dispatch-error">
              {err}
            </div>
          )}
        </div>

        <div className="card p-5">
          <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>// {kind === "export" ? "EXPORT" : "DOMESTIC"} DETAILS</div>
          <div className="space-y-3">
            <div>
              <label>{kind === "export" ? "Importer / End Customer" : "End Customer"}</label>
              <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder={kind === "export" ? "Importer name" : "Buyer name"} data-testid="customer-input" />
            </div>
            <div>
              <label>Vehicle / LR No.</label>
              <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Optional" data-testid="vehicle-input" />
            </div>
            <div>
              <label>Dispatch Batch No.</label>
              <input
                value={dispatchBatchNo}
                onChange={(e) => setDispatchBatchNo(e.target.value)}
                placeholder="Optional — e.g. shipment ref"
                data-testid="dispatch-batch-no-input"
              />
            </div>
            <div>
              <label>Invoice No.</label>
              <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder={kind === "export" ? "Required for export — INV-XXXX" : "Optional"} data-testid="invoice-no-input" />
            </div>
            {kind === "export" && (
              <>
                <div>
                  <label>Container No. *</label>
                  <input value={containerNumber} onChange={(e) => setContainerNumber(e.target.value)} placeholder="ABCD-1234567" data-testid="container-no-input" />
                </div>
                <div>
                  <label>Seal No.</label>
                  <input value={sealNumber} onChange={(e) => setSealNumber(e.target.value)} placeholder="Optional" data-testid="seal-no-input" />
                </div>
              </>
            )}
            <div>
              <label>Notes</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dispatch-notes-input" />
            </div>
          </div>
          <button
            className="btn btn-primary w-full mt-4"
            onClick={submit}
            disabled={loading || selected.length === 0 || !customer.trim()}
            data-testid="confirm-dispatch-button"
          >
            <Truck size={16} /> {loading ? "Recording..." : "Confirm Dispatch"}
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border)" }}>
          <div className="font-heading text-sm font-semibold">DISPATCH REGISTER</div>
          <div className="flex items-center gap-1" style={{ minWidth: 220 }}>
            <Search size={14} color="var(--text-muted)" />
            <input
              type="text"
              placeholder="Search customer, invoice, container, vehicle…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: "6px 10px", fontSize: 13 }}
              data-testid="dispatch-search"
            />
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Kind</th>
                <th>Customer</th>
                <th>Vehicle</th>
                <th>Dispatch Batch</th>
                <th>Invoice</th>
                <th>Container</th>
                <th>Batches</th>
                <th className="text-right">Bags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length === 0 && (
                <tr><td colSpan="10" className="text-center py-8" style={{ color: "var(--text-muted)" }}>
                  {history.length === 0 ? "No dispatches yet." : "No dispatches match your search."}
                </td></tr>
              )}
              {filteredHistory.map((d) => (
                <tr key={d.id} data-testid={`dispatch-row-${d.id}`}>
                  <td className="font-mono text-xs">{d.created_at.slice(0, 16).replace("T", " ")}</td>
                  <td>
                    <span className={`badge ${d.kind === "export" ? "badge-mixed" : "badge-transferred"}`}>
                      {d.kind === "export" ? "EXPORT" : "DOMESTIC"}
                    </span>
                  </td>
                  <td>{d.customer}</td>
                  <td className="font-mono">{d.vehicle || "—"}</td>
                  <td className="font-mono">{d.dispatch_batch_number || "—"}</td>
                  <td className="font-mono text-xs">{d.invoice_number || "—"}</td>
                  <td className="font-mono text-xs">
                    {d.container_number || "—"}
                    {d.seal_number && <span className="block text-[10px]" style={{ color: "var(--text-dim)" }}>seal: {d.seal_number}</span>}
                  </td>
                  <td className="font-mono text-xs">{d.items.map((i) => i.batch_number).join(", ")}</td>
                  <td className="font-mono text-right">{fmtNum(d.total_bags)}</td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "4px 8px", fontSize: 12 }}
                        onClick={() => openDetails(d)}
                        data-testid={`details-dispatch-${d.id}`}
                        title="View full traceability"
                      >
                        <Info size={12} />
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "4px 8px", fontSize: 12 }}
                        onClick={() => openEdit(d)}
                        data-testid={`edit-dispatch-${d.id}`}
                        title="Edit dispatch details"
                      >
                        <Pencil size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,42,78,0.45)" }} onClick={closeEdit}>
          <div className="card p-6 w-full max-w-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-dispatch-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono" style={{ color: "var(--brand-blue)" }}>// EDIT DISPATCH</div>
                <h3 className="font-heading text-xl font-bold mt-1">Update dispatch details</h3>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={closeEdit} data-testid="close-edit-dispatch"><X size={16} /></button>
            </div>

            <div className="flex gap-2 mb-4">
              {KINDS.map((k) => {
                const active = editForm.kind === k.value;
                const Icon = k.icon;
                return (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => updateEdit("kind", k.value)}
                    className="btn flex-1"
                    style={{
                      background: active ? "var(--brand-blue)" : "#fff",
                      color: active ? "#fff" : "var(--text)",
                      border: `1px solid ${active ? "var(--brand-blue)" : "var(--border)"}`,
                    }}
                    data-testid={`edit-kind-${k.value}`}
                  >
                    <Icon size={14} /> {k.label}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label>Customer</label>
                <input value={editForm.customer || ""} onChange={(e) => updateEdit("customer", e.target.value)} data-testid="edit-customer" />
              </div>
              <div>
                <label>Vehicle / LR No.</label>
                <input value={editForm.vehicle || ""} onChange={(e) => updateEdit("vehicle", e.target.value)} data-testid="edit-vehicle" />
              </div>
              <div>
                <label>Dispatch Batch No.</label>
                <input value={editForm.dispatch_batch_number || ""} onChange={(e) => updateEdit("dispatch_batch_number", e.target.value)} data-testid="edit-dispatch-batch-no" />
              </div>
              <div>
                <label>Invoice No.</label>
                <input value={editForm.invoice_number || ""} onChange={(e) => updateEdit("invoice_number", e.target.value)} data-testid="edit-invoice-no" />
              </div>
              {editForm.kind === "export" && (
                <>
                  <div>
                    <label>Container No.</label>
                    <input value={editForm.container_number || ""} onChange={(e) => updateEdit("container_number", e.target.value)} data-testid="edit-container-no" />
                  </div>
                  <div>
                    <label>Seal No.</label>
                    <input value={editForm.seal_number || ""} onChange={(e) => updateEdit("seal_number", e.target.value)} data-testid="edit-seal-no" />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <label>Notes</label>
                <textarea rows="2" value={editForm.notes || ""} onChange={(e) => updateEdit("notes", e.target.value)} data-testid="edit-notes" />
              </div>
            </div>

            {editErr && (
              <div className="card p-3 mt-3" style={{ borderColor: "var(--red)", background: "rgba(239,68,68,0.06)", color: "var(--red)" }} data-testid="edit-dispatch-err">
                {editErr}
              </div>
            )}

            <div className="flex gap-3 mt-5">
              <button className="btn btn-secondary flex-1" onClick={closeEdit} data-testid="cancel-edit-dispatch">Cancel</button>
              <button className="btn btn-primary flex-1" onClick={saveEdit} disabled={editLoading} data-testid="save-edit-dispatch">
                <Save size={14} /> {editLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-traceability side drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(13,42,78,0.45)" }} onClick={() => setDetail(null)}>
          <div className="ml-auto h-full w-full max-w-2xl overflow-auto" style={{ background: "var(--surface)", boxShadow: "var(--shadow-lg)" }} onClick={(e) => e.stopPropagation()} data-testid="dispatch-detail-drawer">
            <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div>
                <div className="text-xs font-mono" style={{ color: "var(--brand-blue)" }}>// DISPATCH TRACEABILITY</div>
                <h3 className="font-heading text-xl font-bold mt-1">
                  {detail.kind === "export" ? "EXPORT" : "DOMESTIC"} → {detail.customer}
                </h3>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setDetail(null)} data-testid="close-details"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Header summary */}
              <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                <div><span style={{ color: "var(--text-muted)" }}>Date:</span> {detail.created_at?.slice(0, 16).replace("T", " ")}</div>
                <div><span style={{ color: "var(--text-muted)" }}>By:</span> {detail.created_by_name || "—"}</div>
                <div><span style={{ color: "var(--text-muted)" }}>Vehicle:</span> {detail.vehicle || "—"}</div>
                <div><span style={{ color: "var(--text-muted)" }}>Dispatch Batch:</span> {detail.dispatch_batch_number || "—"}</div>
                <div><span style={{ color: "var(--text-muted)" }}>Invoice:</span> {detail.invoice_number || "—"}</div>
                <div><span style={{ color: "var(--text-muted)" }}>Container:</span> {detail.container_number || "—"}</div>
                {detail.kind === "export" && (
                  <div><span style={{ color: "var(--text-muted)" }}>Seal:</span> {detail.seal_number || "—"}</div>
                )}
                <div><span style={{ color: "var(--text-muted)" }}>Total bags:</span> {fmtNum(detail.total_bags)} ({fmtNum(detail.total_kg)} kg)</div>
              </div>

              {detail.notes && (
                <div className="p-3 text-xs" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text-muted)" }}>
                  <span style={{ color: "var(--brand-blue)" }}>Notes:</span> {detail.notes}
                </div>
              )}

              {detailLoading && (
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading traceability…</div>
              )}

              {/* Per-item traceability */}
              <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>// BATCHES IN THIS DISPATCH ({detail.items?.length || 0})</div>
              <div className="space-y-3">
                {(detail.items || []).map((i, idx) => (
                  <div key={i.batch_id || idx} className="card p-4" data-testid={`detail-item-${i.batch_number}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-mono font-bold">{i.batch_number}</div>
                        <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                          {fmtNum(i.bags)} bags · {fmtNum(i.bags * 50)} kg · from {i.location}
                        </div>
                      </div>
                      {i.is_mix && (
                        <span className="badge badge-mixed" title="This batch was created by blending other batches">
                          <Blend size={10} /> Blend
                        </span>
                      )}
                      {i.transfer_from && (
                        <span className="badge badge-transferred" title="Was transferred in from another warehouse">
                          <ArrowRightLeft size={10} /> Transferred
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
                      <div><span style={{ color: "var(--text-muted)" }}>Date of Mfg:</span> {i.production_date || "—"}</div>
                      <div><span style={{ color: "var(--text-muted)" }}>Shift:</span> {i.shift?.toUpperCase() || "—"}</div>
                      {i.operator && (
                        <div className="col-span-2"><span style={{ color: "var(--text-muted)" }}>Operator:</span> {i.operator}</div>
                      )}
                      <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                        <span style={{ color: "var(--brand-blue)" }}>QC:</span>{" "}
                        {i.qc_decision === "approved" ? (
                          <>
                            <span style={{ color: "var(--green)" }}>Approved</span>
                            {i.qc_at && <> on <span style={{ color: "var(--text)" }}>{i.qc_at.slice(0, 16).replace("T", " ")}</span></>}
                            {i.qc_by_name && <> by <span style={{ color: "var(--text)" }}>{i.qc_by_name}</span></>}
                          </>
                        ) : (
                          <span style={{ color: "var(--text-muted)" }}>QC info not available</span>
                        )}
                      </div>

                      {i.transfer_from && (
                        <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                          <ArrowRightLeft size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                          <span style={{ color: "var(--brand-blue)" }}>Transferred from:</span>{" "}
                          <strong>{i.transfer_from.batch_number}</strong> @ {i.transfer_from.location}
                          {i.unloaded_at && (
                            <span style={{ color: "var(--text-muted)" }}> · unloaded {i.unloaded_at.slice(0, 16).replace("T", " ")}{i.unloaded_by_name ? ` by ${i.unloaded_by_name}` : ""}</span>
                          )}
                        </div>
                      )}

                      {i.is_mix && i.source_batches && i.source_batches.length > 0 && (
                        <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                          <Blend size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                          <span style={{ color: "var(--brand-blue)" }}>Blended from:</span>{" "}
                          {i.source_batches.map((sb, k) => (
                            <span key={sb.batch_id || sb.batch_number || k}>
                              {k > 0 ? ", " : ""}
                              <strong>{sb.batch_number}</strong>
                              <span style={{ color: "var(--text-muted)" }}> ({sb.bags_taken || sb.bags || "?"}b)</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {i.specs && (
                        <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                          <FlaskConical size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                          <span style={{ color: "var(--brand-blue)" }}>QC Specs:</span>{" "}
                          {[
                            ["Protein", i.specs.protein, "%"],
                            ["Moisture", i.specs.moisture, "%"],
                            ["Sand/Silica", i.specs.sand_silica, "%"],
                            ["Salt", i.specs.salt, "%"],
                            ["Fat", i.specs.fat, "%"],
                            ["TVN", i.specs.volatile_nitrogen, " mg"],
                          ].filter(([_, v]) => v !== null && v !== undefined).map(([k, v, unit], n) => (
                            <span key={k}>
                              {n > 0 ? " · " : ""}
                              {k} <strong>{v}{unit}</strong>
                            </span>
                          ))}
                        </div>
                      )}

                      {i.lab_accurate && (
                        <div className="col-span-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                          + Accurate Lab report on {i.lab_accurate.tested_on || "?"}{i.lab_accurate.tested_by ? ` by ${i.lab_accurate.tested_by}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
